import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import {
  Handshake, Zap, TrendingUp, ChevronRight, Copy, Check, Link as LinkIcon,
  Share2, Trophy, Euro, MousePointerClick, ShoppingBag, Loader2, Sparkles,
  Crown, ExternalLink, BarChart3,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageT } from '../i18n/usePageT';
import { SEO } from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HCAPTCHA_SITEKEY = process.env.REACT_APP_HCAPTCHA_SITEKEY || '10000000-ffff-ffff-ffff-000000000001';

// =============================================================================
// 1. Public Marketing Page + Registration Form — /affiliate-program
// =============================================================================
export const AffiliateProgramPage = () => {
  const { language } = useLanguage();
  const { token, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const T = usePageT();

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    website: '',
    audience_description: '',
    iban: '',
    bic: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      setForm((f) => ({
        ...f,
        full_name: f.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        email: f.email || user.email || '',
      }));
    }
  }, [isAuthenticated, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error(T('Nom et email obligatoires', 'Name and email are required'));
      return;
    }
    if (!captchaToken) {
      toast.error(T('Veuillez valider le captcha', 'Please complete the captcha'));
      return;
    }
    setSubmitting(true);
    try {
      const headers = isAuthenticated && token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/affiliates/register`, { ...form, captcha_token: captchaToken }, { headers });
      setRegistered(data);
      toast.success(T('Bienvenue dans le programme partenaire !', 'Welcome to the partner program!'));
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur lors de l\'inscription', 'Registration error'));
      if (captchaRef.current) captchaRef.current.resetCaptcha();
      setCaptchaToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(T('Copié !', 'Copied!'));
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="affiliate-program-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F4C5C] via-[#0B3A47] to-[#0F4C5C] text-white py-20">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-4">
            <Link to="/" className="hover:text-white">{T('Accueil', 'Home')}</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[#E9C46A]">{T('Programme partenaire', 'Partner program')}</span>
          </nav>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-[#E9C46A]/15 border border-[#E9C46A]/30 rounded-full px-4 py-1.5 mb-5">
              <Handshake className="w-4 h-4 text-[#E9C46A]" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#E9C46A]">{T('Programme d\'affiliation', 'Affiliate Program')}</span>
            </span>
            <SEO
              title={T('Programme d\'affiliation', 'Affiliate Program')}
              description={T(
                'Recommandez Respectful Markets à votre audience et touchez des commissions à vie sur les achats qu\'ils effectuent.',
                'Recommend Respectful Markets to your audience and earn lifetime commissions on their purchases.'
              )}
              url={`${window.location.origin}/affiliate-program`}
            />
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
              {T('Gagnez ', 'Earn ')}
              <span className="text-[#E9C46A]">{T('5% à 12%', '5% to 12%')}</span>
              {T(' sur chaque vente', ' on every sale')}
            </h1>
            <p className="text-xl text-white/80 max-w-2xl">
              {T(
                'Recommandez Respectful Markets à votre audience et touchez des commissions à vie sur les achats qu\'ils effectuent.',
                'Recommend Respectful Markets to your audience and earn lifetime commissions on every purchase they make.',
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="container-main py-16">
        {/* Tiers */}
        <section className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F4C5C] text-center mb-3">
            {T('Deux paliers, des récompenses qui grandissent', 'Two tiers, growing rewards')}
          </h2>
          <p className="text-[#0F4C5C]/70 text-center mb-12 max-w-2xl mx-auto">
            {T(
              'Démarrez en Standard et passez automatiquement en Premium après 10 ventes confirmées.',
              'Start as Standard and auto-upgrade to Premium after 10 confirmed sales.',
            )}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Standard */}
            <Card className="border-[#0F4C5C]/15 hover:shadow-md transition-shadow">
              <CardHeader>
                <Badge variant="outline" className="w-fit mb-2">{T('Niveau de départ', 'Entry tier')}</Badge>
                <CardTitle className="text-2xl text-[#0F4C5C] flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-[#2A9D8F]" />
                  Standard
                </CardTitle>
                <p className="text-4xl font-bold text-[#0F4C5C] mt-3">5%<span className="text-base text-[#0F4C5C]/60 font-normal"> {T('par vente', 'per sale')}</span></p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-[#0F4C5C]/80">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2A9D8F] mt-0.5 flex-shrink-0" /> {T('Inscription gratuite immédiate', 'Free instant registration')}</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2A9D8F] mt-0.5 flex-shrink-0" /> {T('Code unique + liens auto-générés', 'Unique code + auto-generated links')}</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2A9D8F] mt-0.5 flex-shrink-0" /> {T('Tableau de bord stats temps réel', 'Real-time stats dashboard')}</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2A9D8F] mt-0.5 flex-shrink-0" /> {T('Cookie 30 jours', '30-day cookie')}</li>
                </ul>
              </CardContent>
            </Card>

            {/* Premium */}
            <Card className="border-2 border-[#E9C46A] bg-gradient-to-br from-[#E9C46A]/5 to-transparent relative">
              <span className="absolute -top-3 right-4 bg-[#E9C46A] text-[#0F4C5C] text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                {T('Le plus rentable', 'Most lucrative')}
              </span>
              <CardHeader>
                <Badge className="w-fit mb-2 bg-[#E9C46A] text-[#0F4C5C] hover:bg-[#E9C46A]/90">{T('Après 10 ventes', 'After 10 sales')}</Badge>
                <CardTitle className="text-2xl text-[#0F4C5C] flex items-center gap-2">
                  <Crown className="w-6 h-6 text-[#E9C46A]" />
                  Premium
                </CardTitle>
                <p className="text-4xl font-bold text-[#0F4C5C] mt-3">12%<span className="text-base text-[#0F4C5C]/60 font-normal"> {T('par vente', 'per sale')}</span></p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-[#0F4C5C]/80">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#E9C46A] mt-0.5 flex-shrink-0" /> {T('Tout le palier Standard', 'Everything in Standard')}</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#E9C46A] mt-0.5 flex-shrink-0" /> {T('Commission ×2.4 sur chaque vente', 'Commission ×2.4 per sale')}</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#E9C46A] mt-0.5 flex-shrink-0" /> {T('Reversement hebdomadaire Stripe Connect', 'Weekly payout via Stripe Connect')}</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#E9C46A] mt-0.5 flex-shrink-0" /> {T('Support partenaire prioritaire', 'Priority partner support')}</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* How it works */}
        <section className="mb-16 bg-white rounded-3xl p-8 md:p-12 border border-[#0F4C5C]/10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F4C5C] text-center mb-10">
            {T('Comment ça marche ?', 'How it works?')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: Handshake, fr: ['1. Inscrivez-vous', 'Formulaire en 30 secondes, code unique généré'], en: ['1. Register', '30-second form, unique code generated'] },
              { icon: LinkIcon,  fr: ['2. Partagez vos liens', 'Liens prêts à l\'emploi pour produits et boutiques'], en: ['2. Share your links', 'Ready-to-use links for products and stores'] },
              { icon: MousePointerClick, fr: ['3. Vos contacts achètent', 'Cookie 30 jours, vous êtes crédité même plus tard'], en: ['3. Contacts buy', '30-day cookie, you\'re credited even later'] },
              { icon: Euro,       fr: ['4. Touchez vos gains', 'Versement hebdomadaire via Stripe Connect'], en: ['4. Get paid', 'Weekly payout via Stripe Connect'] },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-[#0F4C5C]/10 flex items-center justify-center mb-4">
                  <step.icon className="w-8 h-8 text-[#0F4C5C]" />
                </div>
                <h3 className="font-bold text-[#0F4C5C] mb-1">{T(step.fr[0], step.en[0])}</h3>
                <p className="text-sm text-[#0F4C5C]/70">{T(step.fr[1], step.en[1])}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Registration form */}
        {registered ? (
          <section className="bg-gradient-to-br from-[#2A9D8F] to-[#0F4C5C] rounded-3xl p-8 md:p-12 text-white text-center" data-testid="affiliate-success">
            <div className="w-20 h-20 bg-white/15 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-[#E9C46A]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{T('Félicitations,', 'Congratulations,')} {registered.full_name.split(' ')[0]} !</h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              {T('Votre code partenaire est prêt. Copiez-le et commencez à le partager dès maintenant.',
                 'Your partner code is ready. Copy it and start sharing right now.')}
            </p>
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 max-w-md mx-auto mb-6">
              <p className="text-xs uppercase tracking-widest text-[#E9C46A] mb-2">{T('Votre code', 'Your code')}</p>
              <div className="flex items-center justify-center gap-3">
                <p className="text-4xl font-bold tracking-widest font-mono" data-testid="affiliate-code-display">{registered.code}</p>
                <Button size="icon" variant="ghost" onClick={() => copyToClipboard(registered.code)} className="text-white hover:bg-white/10">
                  <Copy className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {isAuthenticated ? (
                <Button onClick={() => navigate('/affiliate')} className="bg-[#E9C46A] text-[#0F4C5C] hover:bg-[#E9C46A]/90 font-bold px-8" data-testid="go-to-dashboard">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {T('Accéder à mon tableau de bord', 'Go to my dashboard')}
                </Button>
              ) : (
                <Button onClick={() => navigate('/login')} className="bg-[#E9C46A] text-[#0F4C5C] hover:bg-[#E9C46A]/90 font-bold px-8">
                  {T('Se connecter pour voir le tableau de bord', 'Sign in to see your dashboard')}
                </Button>
              )}
            </div>
          </section>
        ) : (
          <section className="bg-white rounded-3xl p-8 md:p-12 border border-[#0F4C5C]/10 shadow-sm">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0F4C5C] text-center mb-3">
                {T('Devenir partenaire', 'Become a partner')}
              </h2>
              <p className="text-[#0F4C5C]/70 text-center mb-8">
                {T('Inscription instantanée, sans engagement, RIB requis pour les paiements.',
                   'Instant registration, no commitment, IBAN required for payments.')}
              </p>
              <form onSubmit={submit} className="space-y-4" data-testid="affiliate-register-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="aff-name">{T('Nom complet', 'Full name')} *</Label>
                    <Input id="aff-name" data-testid="aff-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required className="mt-1.5 h-11" />
                  </div>
                  <div>
                    <Label htmlFor="aff-email">Email *</Label>
                    <Input id="aff-email" type="email" data-testid="aff-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5 h-11" />
                  </div>
                  <div>
                    <Label htmlFor="aff-phone">{T('Téléphone', 'Phone')}</Label>
                    <Input id="aff-phone" data-testid="aff-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11" />
                  </div>
                  <div>
                    <Label htmlFor="aff-web">{T('Site / réseau (URL)', 'Website / social (URL)')}</Label>
                    <Input id="aff-web" type="url" data-testid="aff-website" placeholder="https://..." value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1.5 h-11" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="aff-audience">{T('Décrivez votre audience (optionnel)', 'Describe your audience (optional)')}</Label>
                  <Textarea id="aff-audience" data-testid="aff-audience" rows={3} value={form.audience_description} onChange={(e) => setForm({ ...form, audience_description: e.target.value })} className="mt-1.5 resize-y" placeholder={T('Ex: 25k followers Instagram, blog déco/lifestyle, newsletter de 5k abonnés...', 'Ex: 25k IG followers, lifestyle/home blog, 5k newsletter subscribers...')} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="aff-iban">IBAN (Stripe Connect)</Label>
                    <Input id="aff-iban" data-testid="aff-iban" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="FR76 ..." className="mt-1.5 h-11 font-mono text-sm" />
                  </div>
                  <div>
                    <Label htmlFor="aff-bic">BIC / SWIFT</Label>
                    <Input id="aff-bic" data-testid="aff-bic" value={form.bic} onChange={(e) => setForm({ ...form, bic: e.target.value })} className="mt-1.5 h-11 font-mono text-sm" />
                  </div>
                </div>
                <p className="text-xs text-[#0F4C5C]/60">
                  {T('Vous pourrez compléter votre RIB plus tard depuis le tableau de bord. Sans RIB, les commissions s\'accumulent sans paiement.',
                     'You can complete your IBAN later from the dashboard. Without IBAN, commissions accumulate without payout.')}
                </p>

                <div className="flex justify-center" data-testid="affiliate-hcaptcha-container">
                  <HCaptcha
                    ref={captchaRef}
                    sitekey={HCAPTCHA_SITEKEY}
                    onVerify={(t) => setCaptchaToken(t)}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                    theme="light"
                  />
                </div>

                <Button type="submit" disabled={submitting || !captchaToken} className="btn-primary w-full h-12" data-testid="affiliate-register-submit">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Handshake className="w-4 h-4 mr-2" />}
                  {T('Rejoindre le programme', 'Join the program')}
                </Button>
              </form>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// 2. Affiliate Dashboard — /affiliate (authenticated, after registration)
// =============================================================================
export const AffiliateDashboardPage = () => {
  const { language } = useLanguage();
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const T = usePageT();

  const [aff, setAff] = useState(null);
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    (async () => {
      try {
        const [aRes, lRes] = await Promise.all([
          axios.get(`${API}/affiliates/me`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/affiliates/links`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setAff(aRes.data);
        setLinks(lRes.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('not_registered');
        } else {
          setError(err.response?.data?.detail || 'failed');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated, token, navigate]);

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedLink(key);
    toast.success(T('Lien copié !', 'Link copied!'));
    setTimeout(() => setCopiedLink(null), 1500);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF8F3]">
      <Loader2 className="w-10 h-10 animate-spin text-[#0F4C5C]" />
    </div>
  );

  if (error === 'not_registered') {
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center" data-testid="affiliate-not-registered">
        <Card className="max-w-md text-center border-[#0F4C5C]/10">
          <CardHeader>
            <div className="w-16 h-16 mx-auto rounded-full bg-[#E9C46A]/15 flex items-center justify-center mb-3">
              <Handshake className="w-8 h-8 text-[#0F4C5C]" />
            </div>
            <CardTitle className="text-[#0F4C5C]">{T('Pas encore partenaire', 'Not a partner yet')}</CardTitle>
            <CardDescription>{T('Inscrivez-vous au programme pour commencer à toucher des commissions.', 'Register for the program to start earning commissions.')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="btn-primary" onClick={() => navigate('/affiliate-program')}>
              {T('Rejoindre le programme', 'Join the program')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!aff || !links) return null;

  const tierLabel = aff.tier === 'premium'
    ? { fr: 'Premium', icon: Crown, color: 'bg-[#E9C46A] text-[#0F4C5C]' }
    : { fr: 'Standard', icon: Trophy, color: 'bg-[#2A9D8F] text-white' };
  const TierIcon = tierLabel.icon;

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="affiliate-dashboard">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F4C5C] to-[#0B3A47] text-white py-12">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-3">
            <Link to="/" className="hover:text-white">{T('Accueil', 'Home')}</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[#E9C46A]">{T('Espace partenaire', 'Partner area')}</span>
          </nav>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
                <Handshake className="w-9 h-9 text-[#E9C46A]" />
                {T('Espace partenaire', 'Partner area')}
              </h1>
              <p className="text-white/70 mt-1">{aff.full_name}</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={`${tierLabel.color} font-bold uppercase tracking-wider px-3 py-1`}>
                <TierIcon className="w-3 h-3 mr-1" />
                {tierLabel.fr}
              </Badge>
              <div className="bg-white/10 backdrop-blur rounded-xl px-4 py-2 border border-white/20">
                <p className="text-[10px] uppercase tracking-widest text-white/60">{T('Code', 'Code')}</p>
                <div className="flex items-center gap-2">
                  <p className="text-xl font-bold font-mono" data-testid="affiliate-code">{aff.code}</p>
                  <button onClick={() => copyToClipboard(aff.code, 'code')} className="text-white/70 hover:text-white">
                    {copiedLink === 'code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-main py-10">
        {/* KPI cards */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="affiliate-kpis">
          <Card className="border-[#0F4C5C]/10">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <MousePointerClick className="w-5 h-5 text-[#2A9D8F]" />
                <span className="text-xs text-[#0F4C5C]/60">{T('Clics totaux', 'Total clicks')}</span>
              </div>
              <p className="text-3xl font-bold text-[#0F4C5C]" data-testid="aff-total-clicks">{aff.total_clicks}</p>
            </CardContent>
          </Card>
          <Card className="border-[#0F4C5C]/10">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <ShoppingBag className="w-5 h-5 text-sky-500" />
                <span className="text-xs text-[#0F4C5C]/60">{T('Conversions', 'Conversions')}</span>
              </div>
              <p className="text-3xl font-bold text-[#0F4C5C]" data-testid="aff-total-conversions">{aff.total_conversions}</p>
            </CardContent>
          </Card>
          <Card className="border-[#0F4C5C]/10">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                <span className="text-xs text-[#0F4C5C]/60">{T('Total gagné', 'Total earned')}</span>
              </div>
              <p className="text-3xl font-bold text-emerald-700" data-testid="aff-total-earned">€{(aff.total_earned || 0).toFixed(2)}</p>
            </CardContent>
          </Card>
          <Card className="border-[#E9C46A] bg-[#E9C46A]/5">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Euro className="w-5 h-5 text-[#E9C46A]" />
                <span className="text-xs text-[#0F4C5C]/60">{T('En attente', 'Pending')}</span>
              </div>
              <p className="text-3xl font-bold text-[#0F4C5C]" data-testid="aff-pending-payout">€{(aff.pending_payout || 0).toFixed(2)}</p>
              <p className="text-[10px] text-[#0F4C5C]/60 mt-1">{T('Prochain versement lundi', 'Next payout Monday')}</p>
            </CardContent>
          </Card>
        </section>

        {/* Promotion banner if standard */}
        {aff.tier === 'standard' && (
          <Card className="mb-8 border-[#E9C46A]/40 bg-gradient-to-r from-[#E9C46A]/10 to-transparent">
            <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <Crown className="w-8 h-8 text-[#E9C46A]" />
                <div>
                  <p className="font-bold text-[#0F4C5C]">{T('Passez en Premium', 'Upgrade to Premium')}</p>
                  <p className="text-sm text-[#0F4C5C]/70">
                    {T('Plus que {n} ventes pour passer à 12% de commission',
                       'Just {n} more sales to reach 12% commission').replace('{n}', Math.max(0, 10 - (aff.total_conversions || 0)))}
                  </p>
                </div>
              </div>
              <div className="flex-1 max-w-xs">
                <div className="h-2 bg-[#0F4C5C]/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#E9C46A] to-[#2A9D8F]"
                    style={{ width: `${Math.min(100, ((aff.total_conversions || 0) / 10) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-[#0F4C5C]/60 mt-1 text-right">{aff.total_conversions}/10</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Links generator */}
        <section data-testid="affiliate-links-section">
          <h2 className="text-2xl font-bold text-[#0F4C5C] mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            {T('Mes liens partenaires', 'My partner links')}
          </h2>
          <Tabs defaultValue="global" className="mb-6">
            <TabsList>
              <TabsTrigger value="global" data-testid="links-tab-global">{T('Lien global', 'Global link')}</TabsTrigger>
              <TabsTrigger value="vendors" data-testid="links-tab-vendors">{T('Par vendeur', 'By vendor')} ({links.vendors.length})</TabsTrigger>
              <TabsTrigger value="products" data-testid="links-tab-products">{T('Par produit', 'By product')} ({links.products.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="global">
              <Card className="border-[#0F4C5C]/10">
                <CardContent className="p-5">
                  <p className="text-sm text-[#0F4C5C]/70 mb-3">{T(links.global.label_fr, links.global.label_en)}</p>
                  <div className="flex items-center gap-2 bg-[#FBF8F3] rounded-xl p-3 border border-[#0F4C5C]/10">
                    <LinkIcon className="w-4 h-4 text-[#0F4C5C]/60 flex-shrink-0" />
                    <code className="text-sm text-[#0F4C5C] flex-1 truncate" data-testid="global-link-url">{links.global.url}</code>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(links.global.url, 'global')} data-testid="copy-global-link">
                      {copiedLink === 'global' ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="vendors">
              <Card className="border-[#0F4C5C]/10">
                <CardContent className="p-5">
                  {links.vendors.length === 0 ? (
                    <p className="text-sm text-[#0F4C5C]/60 text-center py-6">{T('Aucun vendeur disponible pour le moment.', 'No vendors available.')}</p>
                  ) : (
                    <div className="space-y-2">
                      {links.vendors.map((v) => (
                        <div key={v.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#FBF8F3]" data-testid={`vendor-link-${v.id}`}>
                          <div className="w-10 h-10 rounded-lg bg-[#0F4C5C]/10 flex items-center justify-center flex-shrink-0">
                            <ShoppingBag className="w-5 h-5 text-[#0F4C5C]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-[#0F4C5C] truncate">{v.name}</p>
                            <code className="text-xs text-[#0F4C5C]/60 truncate block">{v.url}</code>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => copyToClipboard(v.url, `v-${v.id}`)}>
                            {copiedLink === `v-${v.id}` ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="products">
              <Card className="border-[#0F4C5C]/10">
                <CardContent className="p-5">
                  {links.products.length === 0 ? (
                    <p className="text-sm text-[#0F4C5C]/60 text-center py-6">{T('Aucun produit disponible pour le moment.', 'No products available.')}</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {links.products.map((p) => (
                        <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#0F4C5C]/10 bg-white" data-testid={`product-link-${p.id}`}>
                          {p.image ? (
                            <img src={p.image} alt={p.name} className="w-12 h-12 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-12 h-12 rounded bg-[#FBF8F3] flex items-center justify-center flex-shrink-0">
                              <ShoppingBag className="w-5 h-5 text-[#0F4C5C]/40" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#0F4C5C] truncate">{p.name}</p>
                            <p className="text-xs text-[#2A9D8F] font-bold">€{(p.price || 0).toFixed(2)}</p>
                          </div>
                          <div className="flex flex-col gap-1">
                            <Button size="icon" variant="ghost" onClick={() => copyToClipboard(p.url, `p-${p.id}`)} className="h-7 w-7">
                              {copiedLink === `p-${p.id}` ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                            </Button>
                            <a href={p.url.split('?')[0]} target="_blank" rel="noopener noreferrer">
                              <Button size="icon" variant="ghost" className="h-7 w-7">
                                <ExternalLink className="w-3.5 h-3.5" />
                              </Button>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </div>
  );
};
